import config from '@/config';
import api from '@/shared/api';
import createDOMPurify from 'dompurify';
// Open all links inside task documentation in a new tab/window, same as useGetTaskDocumentation.
const sanitizer = createDOMPurify();
sanitizer.addHook('afterSanitizeAttributes', currentNode => {
    if ('target' in currentNode) {
        currentNode.setAttribute('target', '_blank');
        currentNode.setAttribute('rel', 'noopener noreferrer');
    }
});
const escapeHtml = (value) => (value !== null && value !== void 0 ? value : '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
// Used to jump to the right task
const anchorId = (id) => `task-doc-${(id !== null && id !== void 0 ? id : '').replace(/[^a-zA-Z0-9-]/g, '')}`;
// Extra safety
const stripScriptTags = (html) => html.replace(/<script[\s\S]*?<\/script\s*>/gi, '').replace(/<script\b[^>]*>/gi, '');
const fetchDocumentation = async (templateId) => {
    try {
        const { data } = await api.base.get(`${config.appUrls.tasks}/api/taskTemplates/Documentation/${templateId}`);
        return stripScriptTags(sanitizer.sanitize(data || ''));
    }
    catch (_a) {
        // Callers may not always know upfront whether a template actually has documentation, so a
        // failed/404 lookup for one item shouldn't blank out the whole tab (Promise.all would
        // otherwise reject as soon as one of these fails).
        return null;
    }
};
const buildSection = (item, documentation, level) => {
    const heading = level === 'main' ? 'h1' : 'h2';
    return `
  <section id="${anchorId(item.id)}" class="task-section ${level}">
    <${heading}>${escapeHtml(item.name)}</${heading}>
    ${documentation ? `<div class="documentation">${documentation}</div>` : ''}
  </section>`;
};
export const openBlankDocumentationWindow = () => {
    const documentationWindow = window.open('', '_blank');
    if (documentationWindow) {
        documentationWindow.opener = null;
    }
    return documentationWindow;
};
export const openTaskDocumentationTab = async (mainItem, subItems, focusId, documentationWindow = openBlankDocumentationWindow()) => {
    if (!documentationWindow)
        return;
    const [mainDocumentation, subDocumentations] = await Promise.all([
        mainItem.hasDocumentation ? fetchDocumentation(mainItem.templateId) : Promise.resolve(null),
        Promise.all(subItems.map(subItem => subItem.hasDocumentation ? fetchDocumentation(subItem.templateId) : Promise.resolve(null))),
    ]);
    const sections = [
        buildSection(mainItem, mainDocumentation, 'main'),
        ...subItems.map((subItem, index) => buildSection(subItem, subDocumentations[index], 'sub')),
    ].join('\n');
    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(mainItem.name)}</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
        margin: 24px 32px;
        color: #222;
      }
      .task-section { padding: 16px 0; }
      .task-section.sub { border-top: 1px solid #ddd; padding-left: 16px; }
      .task-section h1 { font-size: 22px; margin: 0 0 8px; }
      .task-section h2 { font-size: 16px; margin: 0 0 8px; }
      .documentation { font-size: 14px; line-height: 1.5; }
      .task-section:target { background: #fff8d6; }
    </style>
  </head>
  <body>
    ${sections}
    <script>
      var target = document.getElementById(${JSON.stringify(anchorId(focusId))});
      if (target) { target.scrollIntoView(); }
    </script>
  </body>
</html>`;
    documentationWindow.document.open();
    documentationWindow.document.write(html);
    documentationWindow.document.close();
};
